<template>
  <div v-loading="loading" class="audit-table">
    <b-table
      bordered
      striped
      hover
      responsive
      show-empty
      :items="tableRows"
      :fields="fields"
      :empty-text="loading ? '載入中…' : '沒有符合條件的審查記錄'"
      class="mb-0"
    >
      <template #head()="data">
        <pos-id-sortable-header
          :label="data.field.label"
          :sort-key="data.field.sortKey"
          :active-sort-key="sortKey"
          :direct="sortDirect"
          @sort="handleHeaderSort"
        />
      </template>

      <template #cell(created_at)="row">
        <div class="text-nowrap">{{ displayText(row.item.created_at) }}</div>
      </template>

      <template #cell(circuit_id)="row">
        <div class="text-nowrap">{{ displayText(row.item.circuit_id) }}</div>
      </template>

      <template #cell(pos_id)="row">
        <div class="text-nowrap">{{ displayText(row.item.pos_id) }}</div>
      </template>

      <template #cell(film_id)="row">
        <div class="text-nowrap">{{ displayText(row.item.film_id) }}</div>
      </template>

      <template #cell(film_master_id)="row">
        <div class="text-nowrap">{{ displayText(row.item.film_master_id) }}</div>
      </template>

      <template #cell(title_name)="row">
        <div style="min-width: 160px">{{ displayText(row.item.title_name) }}</div>
      </template>

      <!-- 匹配動作：直接用後端 action_label，不在前端另建枚舉映射（guide §4.1） -->
      <template #cell(action_label)="row">
        <div class="text-nowrap">
          {{ row.item.action_label || displayText(row.item.action) }}
        </div>
      </template>

      <!-- 創建來源：source_label + auto_create 灰底「系統」角標（guide §5） -->
      <template #cell(source_label)="row">
        <div class="d-flex align-items-center flex-wrap">
          <b-badge :variant="auditSourceVariant(row.item.source)" class="mr-1">
            {{ row.item.source_label || displayText(row.item.source) }}
          </b-badge>
          <b-badge v-if="row.item.source === 'auto_create'" variant="light">
            系統
          </b-badge>
        </div>
      </template>

      <!-- 變動類型：create 綠 / update 藍（auditChangeTypeVariant） -->
      <template #cell(change_type_label)="row">
        <b-badge :variant="auditChangeTypeVariant(row.item.change_type)">
          {{ row.item.change_type_label || displayText(row.item.change_type) }}
        </b-badge>
      </template>

      <!-- 改動對照：before → after（只含變化欄位，可能為 null） -->
      <template #cell(before_after)="row">
        <div class="audit-diff" style="min-width: 280px">
          <div
            v-for="pair in row.item._pairs"
            :key="pair.key"
            class="audit-diff__row"
          >
            <span class="audit-diff__key">{{ pair.key }}</span>
            <span class="audit-diff__before">{{ pair.before }}</span>
            <i class="el-icon-right audit-diff__arrow"></i>
            <span class="audit-diff__after">{{ pair.after }}</span>
          </div>
          <div
            v-if="row.item._pairs.length === 0 && !row.item._extraJson"
            class="text-muted"
          >
            —
          </div>
          <details v-if="row.item._extraJson" class="audit-diff__more">
            <summary>其他變動欄位（{{ row.item._extraCount }}）</summary>
            <pre class="audit-diff__json">{{ row.item._extraJson }}</pre>
          </details>
        </div>
      </template>

      <template #cell(reason)="row">
        <div style="min-width: 140px">{{ displayText(row.item.reason) }}</div>
      </template>

      <template #cell(operator_name)="row">
        <div class="text-nowrap">{{ displayText(row.item.operator_name) }}</div>
      </template>

      <template #cell(ip)="row">
        <div class="text-nowrap">{{ displayText(row.item.ip) }}</div>
      </template>

      <!-- 有 film_circuit_id / film_circuit_special_id 時提供跳轉（可選功能） -->
      <template #cell(actions)="row">
        <div class="d-flex flex-wrap" style="min-width: 130px">
          <b-button
            v-if="row.item.film_circuit_id"
            size="sm"
            variant="outline-info"
            class="mr-1 mb-1"
            @click="$emit('show-film-circuit', row.item)"
          >
            匹配記錄
          </b-button>
          <b-button
            v-if="row.item.film_circuit_special_id"
            size="sm"
            variant="outline-primary"
            class="mb-1"
            @click="$emit('show-special-id', row.item)"
          >
            白名單
          </b-button>
          <span
            v-if="!row.item.film_circuit_id && !row.item.film_circuit_special_id"
            class="text-muted"
          >
            —
          </span>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { auditChangeTypeVariant, auditSourceVariant } from '@/utils/posId'
import PosIdSortableHeader from './PosIdSortableHeader.vue'

// before / after 只含「有變動的欄位」，常見欄位優先展開（guide §4.1 / §5）
const COMMON_FIELDS = [
  'pos_id',
  'film_id',
  'master_id',
  'status',
  'remarks',
  'title_name',
  'engagements',
  'gross'
]

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function formatDiffValue(value) {
  if (value === undefined || value === null) {
    return '—'
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return String(value)
    }
  }
  if (value === '') {
    return '(空)'
  }
  return String(value)
}

function collectKeys(before, after) {
  const keys = []
  const push = (source) => {
    if (!isPlainObject(source)) {
      return
    }
    Object.keys(source).forEach((key) => {
      if (keys.indexOf(key) === -1) {
        keys.push(key)
      }
    })
  }
  push(before)
  push(after)
  return keys
}

function buildPairs(item) {
  const before = item && isPlainObject(item.before) ? item.before : null
  const after = item && isPlainObject(item.after) ? item.after : null
  return COMMON_FIELDS.filter((key) => {
    const inBefore = Boolean(before) && Object.prototype.hasOwnProperty.call(before, key)
    const inAfter = Boolean(after) && Object.prototype.hasOwnProperty.call(after, key)
    return inBefore || inAfter
  }).map((key) => ({
    key,
    before: before ? formatDiffValue(before[key]) : '—',
    after: after ? formatDiffValue(after[key]) : '—'
  }))
}

function buildExtra(item) {
  const before = item && isPlainObject(item.before) ? item.before : null
  const after = item && isPlainObject(item.after) ? item.after : null
  const keys = collectKeys(before, after).filter(
    (key) => COMMON_FIELDS.indexOf(key) === -1
  )
  if (keys.length === 0) {
    return { count: 0, json: '' }
  }
  const pick = (source) => {
    const picked = {}
    keys.forEach((key) => {
      if (source && Object.prototype.hasOwnProperty.call(source, key)) {
        picked[key] = source[key]
      }
    })
    return picked
  }
  const json = JSON.stringify(
    { before: before ? pick(before) : null, after: after ? pick(after) : null },
    null,
    2
  )
  return { count: keys.length, json }
}

export default {
  name: 'AuditTable',
  components: {
    PosIdSortableHeader
  },
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    sortKey: {
      type: String,
      default: ''
    },
    sortDirect: {
      type: String,
      default: 'desc'
    }
  },
  data() {
    return {
      baseFields: [
        { key: 'created_at', label: '時間', sortKey: 'created_at' },
        { key: 'circuit_id', label: '院線', sortKey: 'circuit_id' },
        { key: 'pos_id', label: 'pos-id', sortKey: 'pos_id' },
        { key: 'film_id', label: 'film_id', sortKey: 'film_id' },
        { key: 'film_master_id', label: 'master' },
        { key: 'title_name', label: '標題' },
        { key: 'action_label', label: '匹配動作', sortKey: 'action' },
        { key: 'source_label', label: '創建來源', sortKey: 'source' },
        { key: 'change_type_label', label: '變動類型', sortKey: 'change_type' },
        { key: 'before_after', label: '改動對照' },
        { key: 'reason', label: '緣由' },
        { key: 'operator_name', label: '操作人', sortKey: 'operator_name' },
        { key: 'ip', label: 'IP' },
        { key: 'actions', label: '操作' }
      ]
    }
  },
  computed: {
    // 當前排序列的表頭以 variant=info 高亮（配合 PosIdSortableHeader 的 ↑ / ↓ 標誌）
    fields() {
      return this.baseFields.map((field) => {
        if (field.sortKey && field.sortKey === this.sortKey) {
          return Object.assign({}, field, { variant: 'info' })
        }
        return field
      })
    },
    // 為每一列預先算好 before/after 對照，避免在 template 反覆計算
    tableRows() {
      return (this.rows || []).map((item) => {
        const extra = buildExtra(item)
        return Object.assign({}, item, {
          _pairs: buildPairs(item),
          _extraCount: extra.count,
          _extraJson: extra.json
        })
      })
    }
  },
  methods: {
    handleHeaderSort(sortKey) {
      if (!sortKey) {
        return
      }
      this.$emit('sort-changed', sortKey)
    },
    displayText(value) {
      if (value === undefined || value === null || value === '') {
        return '—'
      }
      return String(value)
    },
    auditChangeTypeVariant,
    auditSourceVariant
  }
}
</script>

<style lang="scss" scoped>
.audit-table {
  .audit-diff {
    font-size: 12px;
  }
  .audit-diff__row {
    display: flex;
    align-items: baseline;
    // 使用者回饋（2026-10-13）：改動對照的 before/after 是自由文字（標題/備註），
    // nowrap 會讓該欄寬度無上限並把整張表推向橫向捲動 → 改為換行顯示。
    white-space: normal;
    word-break: break-word;
  }
  .audit-diff__key {
    color: #6c757d;
    margin-right: 4px;
  }
  .audit-diff__before {
    color: #b02a37;
    text-decoration: line-through;
  }
  .audit-diff__after {
    color: #1e7e34;
    font-weight: 600;
  }
  .audit-diff__arrow {
    margin: 0 4px;
    color: #adb5bd;
    font-size: 12px;
  }
  .audit-diff__more {
    margin-top: 4px;
  }
  .audit-diff__json {
    max-width: 420px;
    max-height: 220px;
    overflow: auto;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    padding: 4px;
    margin: 0;
    font-size: 11px;
  }
}
</style>
